import { Fragment, useState } from 'react';
import { InView } from 'react-intersection-observer';

import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';

import { Link, Text } from '@spotify-internal/encore-web';
import { useLabelsForResource } from '@spotify/confidence-common-react';
import {
  ConfirmDialog,
  FiltersProvider,
  ListEmptyState,
  ResourceTable,
} from '@spotify/confidence-core-react';

import {
  findCollectionEntryExtension,
  useCollectionEntryExtensions,
} from '../../extensions';
import { useCollectionEntries, useDeleteCollectionEntry } from '../../hooks';
import { FavoritesProvider } from '../FavoritesProvider';
import { CollectionEntriesToolbar } from './CollectionEntriesToolbar';
import { CollectionEntryRow } from './CollectionEntryRow';
import { HidableColumn } from './HidableColumn';
import { useCollectionEntriesFilter } from './useCollectionEntriesFilter';

import styles from './CollectionEntriesList.module.css';

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
  metricsUrl?: string;
};

type EntryToRemove = {
  name: string;
  displayName: string;
};

const EntriesListContent = ({
  collectionName,
  isFavorites,
  metricsUrl,
}: CollectionEntriesListProps) => {
  const extensions = useCollectionEntryExtensions();
  const [entryToRemove, setEntryToRemove] = useState<EntryToRemove | null>(
    null,
  );

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const {
    filteredEntries,
    searchQuery,
    setQuery,
    typeFilter,
    availableTypes,
    selectType,
    allColumnIds,
    activeColumns,
    hiddenColumns,
    toggleColumn,
    isColumnVisible,
    hasActiveFilters,
  } = useCollectionEntriesFilter(collectionEntries, extensions);

  const { labelDefinitions } = useLabelsForResource({});

  const { deleteCollectionEntry } = useDeleteCollectionEntry();

  const toolbar = (
    <CollectionEntriesToolbar
      searchQuery={searchQuery}
      onSearchChange={setQuery}
      typeFilter={typeFilter}
      availableTypes={availableTypes}
      onTypeSelect={selectType}
      allColumnIds={allColumnIds}
      hiddenColumns={hiddenColumns}
      onToggleColumn={toggleColumn}
    />
  );

  if (isEmpty && !hasActiveFilters) {
    return (
      <>
        {toolbar}
        <ListEmptyState
          title={isFavorites ? 'No favorites yet' : 'No items yet'}
          description={
            isFavorites ? (
              'Bookmark a resource to add it to your favorites.'
            ) : (
              <>
                Browse{' '}
                {metricsUrl ? (
                  <Link href={metricsUrl} data-track-source-anchor="34e6440b9f">metrics</Link>
                ) : (
                  'metrics'
                )}{' '}
                to add to this collection.
              </>
            )
          }
        />
      </>
    );
  }

  return (
    <Fragment>
      {toolbar}
      <ResourceTable.StickyContainer
        loading={isLoading && !collectionEntries?.length}
        error={error}
      >
        <ResourceTable.Root className={styles.fixedLayout}>
          <ResourceTable.StickyHead>
            <ResourceTable.HeaderRow>
              <HidableColumn id="favorite" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell width={88}>
                  Favorite
                </ResourceTable.HeaderCell>
              </HidableColumn>
              <HidableColumn id="name" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell>Name</ResourceTable.HeaderCell>
              </HidableColumn>
              <HidableColumn id="type" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell width="15%">
                  Type
                </ResourceTable.HeaderCell>
              </HidableColumn>
              {activeColumns?.map(col => (
                <ResourceTable.HeaderCell key={col.id}>
                  {capitalize(lowerCase(col.id))}
                </ResourceTable.HeaderCell>
              ))}
              <HidableColumn id="owner" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell>Owner</ResourceTable.HeaderCell>
              </HidableColumn>
              <HidableColumn id="labels" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell>Labels</ResourceTable.HeaderCell>
              </HidableColumn>
              <HidableColumn id="added" isVisible={isColumnVisible}>
                <ResourceTable.HeaderCell width="15%">
                  Added
                </ResourceTable.HeaderCell>
              </HidableColumn>
              <ResourceTable.HeaderCell width={56} />
            </ResourceTable.HeaderRow>
          </ResourceTable.StickyHead>
          <ResourceTable.Body>
            {filteredEntries.map(entry => {
              const extension = findCollectionEntryExtension(extensions, entry);
              return (
                <CollectionEntryRow
                  key={entry.name}
                  entry={entry}
                  extension={extension}
                  columns={activeColumns}
                  labelDefinitions={labelDefinitions}
                  isColumnVisible={isColumnVisible}
                  onRemoveEntry={setEntryToRemove}
                />
              );
            })}
          </ResourceTable.Body>
        </ResourceTable.Root>
      </ResourceTable.StickyContainer>
      {filteredEntries.length === 0 &&
        hasActiveFilters &&
        !isLoading &&
        !hasNextPage &&
        !error && (
          <ListEmptyState
            title="No matching entries"
            description="Try a different search or filter."
          />
        )}
      {hasNextPage && (
        <InView
          key={collectionEntries?.length}
          onChange={async inView => {
            if (inView) await nextPage();
          }}
         data-track-source-anchor="18344c698b;entries-list-content-in-view"/>
      )}
      {entryToRemove && (
        <ConfirmDialog
          title="Remove from collection"
          action="Remove"
          onClose={() => setEntryToRemove(null)}
          onConfirm={() => deleteCollectionEntry(entryToRemove.name)}
         data-track-source-anchor="e373f0fcc8;entries-list-content-confirm-dialog">
          <Text variant="bodyMedium">
            Are you sure you want to remove{' '}
            <strong>{entryToRemove.displayName}</strong> from this collection?
          </Text>
        </ConfirmDialog>
      )}
    </Fragment>
  );
};

export const CollectionEntriesList = (props: CollectionEntriesListProps) => (
  <FavoritesProvider enabled>
    <FiltersProvider
      searchField="displayName"
      persistence="session"
      disableQueryParams
    >
      <EntriesListContent {...props} />
    </FiltersProvider>
  </FavoritesProvider>
);
